import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Accordion,
  Modal,
  InputGroup
} from "react-bootstrap";
import { useTheme } from "../ThemeContext";
import { Home as Building, X, Check,Shield } from "react-feather";
import "./DomainScanner.css";
import { apifetch } from '../services/interceptors';
import { INTERNAL_REPORT_PREVIEW_KEY } from './InternalReportPreview';
import { riskBandFor, bandStyleScore } from './report/shapeReport';
import MODULES from "./ExternalRiskModules";
import PublicIpOptions from "./PublicIpOptions";
import { publicIpRequest } from "../utils/publicIpTargets";


const DomainScanner = () => {
  const [domain, setDomain] = useState("");
  const [publicIp, setPublicIp] = useState("");
  const [publicIpMode, setPublicIpMode] = useState("auto");

  const [loading, setLoading] = useState(false);  
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [modules, setModules] = useState([]);
  
  const token = localStorage.getItem("token");
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [hibpEmails, setHibpEmails] = useState("");
  
  // Organization creation states
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [website, setWebsite] = useState("");
  const [primaryPerson, setPrimaryPerson] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [orgError, setOrgError] = useState("");
  const [orgMessage, setOrgMessage] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [exporting, setExporting] = useState(false);
  
  
  const { isDarkMode } = useTheme();

  const fetchOrganizations = async () => {
    try {
      const response = await apifetch("s1/api/organizations", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setError("Failed to fetch organizations");
        return;
      }
      const data = await response.json();
      setOrganizations(data.organizations);
      if (data.organizations.length > 0) {
        setSelectedOrganization(data.organizations[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Tenant branding (logo, contact info, brand colors) so the internal deck is presented
  // with the client's own identity — same source the customer-facing report uses.
  const [tenantBranding, setTenantBranding] = useState(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apifetch('s1/api/tenant', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const t = await res.json();
        if (cancelled || !t) return;
        setTenantBranding({
          name: t.tenant_name || null,
          domain: t.tenant_domain || null,
          website: t.website || null,
          address: t.address || null,
          logo: t.large_logo_url || t.logo_url || null,
          phone: t.contact_phone || null,
          email: t.contact_email || null,
          tagline: t.report_tagline || null,
          brandColors: Array.isArray(t.brand_colors) ? t.brand_colors : []
        });
      } catch (e) {
        // Branding is optional — the report falls back to its default look.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createOrganization = async (e) => {
    e.preventDefault();
    setCreatingOrg(true);
    setOrgError("");
    setOrgMessage("");
    
    try {
      const response = await apifetch('s1/api/create_organization', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          primaryPerson,
          primaryEmail,
          primaryPhone,
          website,
          name: organizationName
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setOrgError(data.error || "Can't create organization");
      } else {
        // Refresh organization list
        await fetchOrganizations();
        // Select the newly created organization
        if (data.organization && data.organization.id) {
          setSelectedOrganization(data.organization.id);
        }
        setOrgMessage('Organization created successfully');
        // Reset form
        setOrganizationName('');
        setWebsite('');
        setPrimaryPerson('');
        setPrimaryEmail('');
        setPrimaryPhone('');
        // Close modal after 1.5 seconds
        setTimeout(() => {
          setShowOrgModal(false);
          setOrgMessage("");
        }, 1500);
      }
    } catch (error) {
      setOrgError(`Error creating organization: ${error.message}`);
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!domain) return;

    setLoading(true);
    setError("");
    setResults(null);

    try {
      const response = await apifetch(`s2/api/domain-scan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          target: domain,
          targetType: "domain",
          modules,
          oneTime: true,
          organizationId: selectedOrganization,
          ...publicIpRequest({ target: domain, enabled: modules.includes("publicIp"), mode: publicIpMode, manual: publicIp }),
          hibpEmails:hibpEmails
    .trim()
    .split(/\s+/)
    .filter(Boolean),
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Scan failed");

      setResults(data);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      setError(err.message || "Failed to scan domain");
    } finally {
      setLoading(false);
    }
  };

  const severityVariant = (severity) => {
    if (severity === "critical" || severity < 40) return "danger";
    if (severity === "high" || severity < 50) return "warning";
    if (severity === "medium" || severity < 70) return "info";
    return "success";
  };
  
  const toggleModule = (m) =>
    setModules((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));

  const allSelected = modules.length === MODULES.length;

  const toggleAllModules = () => {
    setModules(allSelected ? [] : MODULES.map(m => m.id));
  };

  const resetOrgForm = () => {
    setOrganizationName("");
    setWebsite("");
    setPrimaryPerson("");
    setPrimaryEmail("");
    setPrimaryPhone("");
    setOrgError("");
    setOrgMessage("");
  };
const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };
   const loadPDFLibraries = async () => {
      const loadScript = (src) => {
        return new Promise((resolve, reject) => {
          if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
          }
          const script = document.createElement('script');
          script.src = src;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      };
  
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      window.jspdf = window.jspdf || window.jsPDF;
    };
    
  // Open the slide-deck presentation in its own tab instead of squeezing full-page-height
  // slides into the dashboard's normal page flow. The scan data is handed off via
  // sessionStorage (tab-scoped, never uploaded) rather than a URL, since results can be large.
  const previewFullReport = () => {
    if (!results) {
      showToast('No result available', 'error');
      return;
    }
    try {
      sessionStorage.setItem(INTERNAL_REPORT_PREVIEW_KEY, JSON.stringify({
        results,
        provider: tenantBranding,
        theme: isDarkMode ? 'dark' : 'light'
      }));
    } catch (e) {
      showToast('Could not open preview (report may be too large)', 'error');
      return;
    }
    // Intentionally no 'noopener': sessionStorage is only copied into a tab opened via
    // window.open() when the new tab stays in the same browsing-context group, which
    // 'noopener' would break — and we need that copy to read the data back out. Safe here
    // since we're only ever navigating to our own /internal-report-preview route.
    window.open('/internal-report-preview', '_blank');
  };

  const exportPdf = async () => {
      if (!results) {
        showToast('No result available', 'error');
        return;
      }
  
      setExporting(true);

      showToast('Generating security report...', 'info');
  
      try {
        const domain = results.domain.replace(/^https?:\/\//, '').split('/')[0];
        const isCyberMode = false;
  
        // ===== DEFINE ALL STATS AND VARIABLES FIRST =====
        // Get severity stats
        const getSeverityStats = (categories) => {
          const stats = { critical: 0, high: 0, medium: 0, low: 0 };
          categories?.forEach(cat => {
            cat.findings?.forEach(f => {
              const s = f.severity?.toLowerCase();
              if (stats[s] !== undefined) stats[s]++;
            });
          });
          return stats;
        };
  
        // Get risk level with futuristic colors
        const getRiskLevelWithColor = (score) => {
          if (score >= 75) return {
            label: 'LOW',
            color: '#4b746fff',
            gradient: 'linear-gradient(135deg, #2ec4b6 0%, #6cdfd3 100%)',
            glow: '0 0 20px rgba(46, 196, 182, 0.5)'
          };
          if (score >= 60) return {
            label: 'MEDIUM',
            color: '#eab308',
            gradient: 'linear-gradient(135deg, #eab308 0%, #facc15 100%)',
            glow: '0 0 20px rgba(234, 179, 8, 0.5)'
          };
          if (score >= 40) return {
            label: 'HIGH',
            color: '#ff9f1c',
            gradient: 'linear-gradient(135deg, #ff9f1c 0%, #ffbf69 100%)',
            glow: '0 0 20px rgba(255, 159, 28, 0.5)'
          };
          return {
            label: 'CRITICAL',
            color: '#ff3b6f',
            gradient: 'linear-gradient(135deg, #ff3b6f 0%, #ff8e8e 100%)',
            glow: '0 0 20px rgba(255, 59, 111, 0.5)'
          };
        };
  
        // Get favicon URL
        const getFaviconUrl = (domain) => {
          try {
            const clean = domain.replace(/^https?:\/\//, '').split('/')[0];
            return `https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${clean}&size=128`;
          } catch {
            return null;
          }
        };
  
  
        // ===== CALCULATE STATS ONCE =====
  
        // 1. Collect and De-duplicate findings globally based on title and description
        const uniqueFindingsMap = new Map();
        results.categories?.forEach(cat => {
          cat.findings?.forEach(f => {
            const key = `${f.title?.trim()}-${f.description?.trim()}`.toLowerCase();
            if (!uniqueFindingsMap.has(key)) {
              // Normalize severity: default to low if missing/unknown
              let severity = f.severity?.toLowerCase() || 'low';
              if (!['critical', 'high', 'medium', 'low'].includes(severity)) {
                severity = 'low';
              }
              uniqueFindingsMap.set(key, {
                ...f,
                severity,
                category: cat.name,
                icon: cat.icon
              });
            }
          });
        });
        const allFindings = Array.from(uniqueFindingsMap.values());
  
        // 2. Sort findings by category then severity priority
        const severityPriority = { critical: 0, high: 1, medium: 2, low: 3 };
        allFindings.sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return (severityPriority[a.severity] ?? 99) - (severityPriority[b.severity] ?? 99);
        });
  
        // 3. Calculate global stats from unique findings
        const stats = { critical: 0, high: 0, medium: 0, low: 0 };
        allFindings.forEach(f => {
          stats[f.severity]++;
        });
  
        const totalFindings = allFindings.length;
        const criticalCount = stats.critical;
        const highCount = stats.high;
        const mediumCount = stats.medium;
        const lowCount = stats.low;
  
        // 4. Re-map categories to only contain their unique findings for the table
        const normalizedCategories = results.categories?.map(cat => {
          const catFindings = allFindings.filter(f => f.category === cat.name);
          return { ...cat, findings: catFindings };
        }).filter(cat => cat.findings.length > 0);
  
        // 5. Security Score — from the backend, not recomputed. The old averaging model
        // here counted passing checks as 100 points each and overwrote results.riskScore,
        // producing a different number than the app displayed. 100 = risk free, 0 = critical.
        const calculatedRiskScore = Number.isFinite(results.riskScore) ? results.riskScore : 100;
        // Style tier follows the clamped BAND, not the raw number, so a low score with
        // no critical findings is never labelled Critical. Displayed score is unchanged.
        const risk = getRiskLevelWithColor(bandStyleScore(riskBandFor(results, calculatedRiskScore)));
  
        // Futuristic gradient backgrounds
        const getBgGradient = () => {
          if (isCyberMode) {
            return 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)';
          }
          if (isDarkMode) {
            return 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)';
          }
          return 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
        };
  
        const getCardGradient = () => {
          if (isCyberMode) {
            return 'linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(123, 97, 255, 0.1) 100%)';
          }
          if (isDarkMode) {
            return 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)';
          }
          return 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)';
        };
  
        // Build futuristic executive summary
        const buildExecutiveSummary = () => {
          const mostSevere = Object.entries(stats)
            .sort((a, b) => b[1] - a[1])[0] || ['low', 0];
  
          const criticalPct = totalFindings
            ? Math.round(((stats.critical + stats.high) / totalFindings) * 100)
            : 0;
  
          return `
          <div style="
            margin-top: 28px;
            padding: 28px;
            border-radius: 24px;
            background: ${getCardGradient()};
            border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
            backdrop-filter: blur(10px);
            box-shadow: 0 8px 32px rgba(0,0,0,0.1);
            position: relative;
            overflow: hidden;
          ">
            <div style="
              position: absolute;
              top: 0;
              right: 0;
              width: 200px;
              height: 200px;
              background: radial-gradient(circle, ${risk.color}15 0%, transparent 70%);
              border-radius: 50%;
              transform: translate(50px, -50px);
            "></div>
            
            <div style="position: relative; z-index: 1;">
              <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  background: ${risk.color};
                  box-shadow: ${risk.glow};
                "></div>
                <span style="
                  font-size: 14px;
                  font-weight: 600;
                  letter-spacing: 2px;
                  text-transform: uppercase;
                  color: ${risk.color};
                ">Executive Analysis</span>
              </div>
              
              <h3 style="
                margin: 0 0 16px;
                font-size: 28px;
                color:${isDarkMode ? '#0f172a' : '#f1f5f9'};
                font-weight: 700;
                letter-spacing: -0.5px;
                background: linear-gradient(135deg, ${isDarkMode ? '#fff' : '#0f172a'}, ${risk.color});
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
              ">
                Security Posture Assessment
              </h3>
              
              <p style="
                font-size: 15px;
                line-height: 1.7;
                color: ${isDarkMode ? '#e2e8f0' : '#334155'};
                margin-bottom: 20px;
              ">
                <span style="
                  display: inline-block;
                  padding: 4px 12px;
                  border-radius: 100px;
                  background: ${risk.color}20;
                  color: ${risk.color};
                  font-weight: 700;
                  margin-right: 8px;
                ">${domain}</span>
                exhibits a <strong style="color: ${risk.color};">${risk.label}</strong> risk profile 
                with a security score of <strong style="color: ${risk.color};">${results.riskScore || 50}/100</strong>.
              </p>
  
              <div style="
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 16px;
                margin-top: 24px;
              ">
                <div style="
                  padding: 16px;
                  border-radius: 16px;
                  background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
                  border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
                ">
                  <div style="font-size: 28px; font-weight: 800; color: ${risk.color}; margin-bottom: 4px;">
                    ${criticalPct}%
                  </div>
                  <div style="font-size: 12px; opacity: 0.7; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
                    High-Impact Issues
                  </div>
                </div>
                <div style="
                  padding: 16px;
                  border-radius: 16px;
                  background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
                  border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
                ">
                  <div style="font-size: 28px; font-weight: 800; color: ${risk.color}; margin-bottom: 4px;">
                    ${mostSevere[1]}
                  </div>
                  <div style="font-size: 12px; opacity: 0.7; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
                    ${mostSevere[0].toUpperCase()} Severity Issues
                  </div>
                </div>
                <div style="
                  padding: 16px;
                  border-radius: 16px;
                  background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
                  border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
                ">
                  <div style="font-size: 28px; font-weight: 800; color: ${risk.color}; margin-bottom: 4px;">
                    ${stats.critical}
                  </div>
                  <div style="font-size: 12px; opacity: 0.7; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
                    Critical Vulnerabilities
                  </div>
                </div>
              </div>
  
              <div style="
                margin-top: 24px;
                padding: 20px;
                border-radius: 16px;
                background: ${isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)'};
                border-left: 4px solid ${risk.color};
              ">
                <div style="display: flex; gap: 12px; align-items: flex-start;">
                  <div style="font-size: 20px;">⚡</div>
                  <div>
                    <div style="font-weight: 700; margin-bottom: 4px; color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};">
                      ${stats.critical ? 'Immediate Action Required' : 'Proactive Security Posture'}
                    </div>
                    <div style="font-size: 13px; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
                      ${stats.critical
              ? `${stats.critical} critical vulnerabilities require immediate remediation to prevent potential breaches.`
              : `No critical vulnerabilities detected. Continue maintaining strong security practices.`
            }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
        };
  
        // Futuristic info card
        const infoCard = (label, value, icon) => `
        <div style="
          padding: 20px;
          border-radius: 20px;
          background: ${getCardGradient()};
          border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        ">
          <div style="
            position: absolute;
            top: 0;
            right: 0;
            width: 100px;
            height: 100px;
            background: radial-gradient(circle, ${isDarkMode ? '#8b5cf620' : '#8b5cf610'} 0%, transparent 70%);
            border-radius: 50%;
            transform: translate(30px, -30px);
          "></div>
          <div style="position: relative; z-index: 1;">
            <div style="font-size: 24px; margin-bottom: 8px;">${icon}</div>
            <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.6; color: ${isDarkMode ? '#94a3b8' : '#64748b'}; margin-bottom: 4px;">
              ${label}
            </div>
            <div style="font-size: 16px; font-weight: 700; color: ${isDarkMode ? '#f1f5f9' : '#0f172a'}; word-break: break-word;">
              ${value}
            </div>
          </div>
        </div>
      `;
  
        // Severity badge
        const severityBadge = (severity, count) => {
          const colors = {
            critical: { bg: '#ff3b6f20', color: '#ff3b6f', icon: '🔴' },
            high: { bg: '#ff9f1c20', color: '#ff9f1c', icon: '🟠' },
            medium: { bg: '#eab30820', color: '#eab308', icon: '🟡' },
            low: { bg: '#2ec4b620', color: '#2ec4b6', icon: '🔵' }
          };
          const c = colors[severity] || colors.low;
  
          return `
          <div style="
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 14px;
            border-radius: 100px;
            background: ${c.bg};
            border: 1px solid ${c.color}30;
          ">
            <span style="font-size: 14px;">${c.icon}</span>
            <span style="font-size: 13px; font-weight: 600; color: ${c.color};">${count}</span>
            <span style="font-size: 12px; opacity: 0.8; color: ${isDarkMode ? '#e2e8f0' : '#334155'};">${severity}</span>
          </div>
        `;
        };
  
        // Build category row with futuristic design
        const buildRow = (cat) => {
          const catStats = { critical: 0, high: 0, medium: 0, low: 0 };
          cat.findings?.forEach(f => {
            const s = f.severity?.toLowerCase();
            if (catStats[s] !== undefined) catStats[s]++;
          });
          const total = Object.values(catStats).reduce((a, b) => a + b, 0);
  
          return `
          <tr style="
            background: ${isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)'};
            border-bottom: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
            transition: all 0.2s ease;
          ">
            <td style="
              padding: 16px 12px;
              font-weight: 600;
              color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
              border-radius: 12px 0 0 12px;
            ">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 20px;">${cat.icon || '🔍'}</span>
                <span>${cat.name}</span>
              </div>
            </td>
            <td align="center" style="padding: 16px 12px;">
              <span style="
                display: inline-block;
                padding: 4px 10px;
                border-radius: 100px;
                background: ${catStats.critical ? '#ff3b6f20' : 'transparent'};
                color: ${catStats.critical ? '#ff3b6f' : '#9ca3af'};
                font-weight: ${catStats.critical ? '700' : '400'};
              ">${catStats.critical || '0'}</span>
            </td>
            <td align="center" style="padding: 16px 12px;">
              <span style="
                display: inline-block;
                padding: 4px 10px;
                border-radius: 100px;
                background: ${catStats.high ? '#ff9f1c20' : 'transparent'};
                color: ${catStats.high ? '#ff9f1c' : '#9ca3af'};
                font-weight: ${catStats.high ? '700' : '400'};
              ">${catStats.high || '0'}</span>
            </td>
            <td align="center" style="padding: 16px 12px;">
              <span style="
                display: inline-block;
                padding: 4px 10px;
                border-radius: 100px;
                background: ${catStats.medium ? '#eab30820' : 'transparent'};
                color: ${catStats.medium ? '#eab308' : '#9ca3af'};
                font-weight: ${catStats.medium ? '700' : '400'};
              ">${catStats.medium || '0'}</span>
            </td>
            <td align="center" style="padding: 16px 12px;">
              <span style="
                display: inline-block;
                padding: 4px 10px;
                border-radius: 100px;
                background: ${catStats.low ? '#2ec4b620' : 'transparent'};
                color: ${catStats.low ? '#2ec4b6' : '#9ca3af'};
                font-weight: ${catStats.low ? '700' : '400'};
              ">${catStats.low || '0'}</span>
            </td>
            <td align="center" style="
              padding: 16px 12px;
              font-weight: 800;
              color: ${isDarkMode ? '#a78bfa' : '#8b5cf6'};
              border-radius: 0 12px 12px 0;
            ">
              <span style="
                display: inline-block;
                padding: 4px 12px;
                background: ${isDarkMode ? '#8b5cf620' : '#8b5cf610'};
                border-radius: 100px;
              ">${total}</span>
            </td>
          </tr>
        `;
        };
  
        // Create paginated content with perfect breaks
        const createPaginatedContent = () => {
          const pages = [];
  
          // PAGE 1 - Cover and Summary
          let page1 = `
          <div style="
            color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            position: relative;
          ">
            <!-- Futuristic Header -->
            <div style="
              position: relative;
              padding: 32px 32px 28px;
              border-radius: 32px;
              background: ${isCyberMode
              ? 'linear-gradient(135deg, #00ff8820 0%, #7b61ff20 100%)'
              : `linear-gradient(135deg, ${isDarkMode ? '#1e293b' : '#8b5cf6'}, ${isDarkMode ? '#0f172a' : '#7c3aed'})`
            };
              color: white;
              margin-bottom: 28px;
              overflow: hidden;
              box-shadow: 0 20px 40px rgba(0,0,0,0.2);
            ">
              <div style="
                position: absolute;
                top: -50px;
                right: -50px;
                width: 200px;
                height: 200px;
                background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%);
                border-radius: 50%;
              "></div>
              <div style="
                position: absolute;
                bottom: -50px;
                left: -50px;
                width: 150px;
                height: 150px;
                background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
                border-radius: 50%;
              "></div>
              
              <div style="position: relative; z-index: 1; display: flex; justify-content: space-between; align-items: center;">
                <div style="display:flex;align-items:center;gap:14px">
              <div>
                    <div style="
                      font-size: 14px;
                      font-weight: 600;
                      letter-spacing: 3px;
                      text-transform: uppercase;
                      opacity: 0.9;
                      margin-bottom: 8px;
                    ">SECURITY REPORT</div>
                    <h1 style="margin: 0; font-size: 36px; font-weight: 800; letter-spacing: -1px; line-height: 1.1;">
                      ${domain}
                    </h1>
                    <p style="margin-top: 8px; font-size: 15px; opacity: 0.9; display: flex; align-items: center; gap: 8px;">
                      <span>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      <span style="display: inline-block; width: 4px; height: 4px; background: white; border-radius: 50%; opacity: 0.5;"></span>
                    </p>
                  </div>
                </div>
                
                <div style="
                  text-align: right;
                  background: rgba(255,255,255,0.1);
                  padding: 20px;
                  border-radius: 24px;
                  backdrop-filter: blur(10px);
                  border: 1px solid rgba(255,255,255,0.2);
                ">
                  <div style="font-size: 56px; font-weight: 900; line-height: 1; letter-spacing: -2px;">
                    ${results.riskScore}<span style="font-size: 20px; opacity: 0.8;">/100</span>
                  </div>
                  <div style="
                    margin-top: 8px;
                    padding: 6px 16px;
                    border-radius: 100px;
                    background: ${risk.color}30;
                    color: white;
                    font-size: 14px;
                    font-weight: 700;
                    letter-spacing: 1px;
                  ">
                    ${risk.label} RISK
                  </div>
                </div>
              </div>
            </div>
  
            <!-- Info Grid -->
            <div style="
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 16px;
              margin-bottom: 24px;
            ">
              ${infoCard('Target', domain, '🌐')}
              ${infoCard('Scan Date', new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), '📅')}
              ${infoCard('Total Findings', totalFindings.toString(), '⚠️')}
              ${infoCard('Risk Level', risk.label, risk.label === 'CRITICAL' ? '🔴' : risk.label === 'ELEVATED' ? '🟠' : '🟢')}
            </div>
  
            <!-- Executive Summary -->
            ${buildExecutiveSummary()}
  
            <!-- Severity Distribution -->
            <div style="
              margin-top: 28px;
              padding: 28px;
              border-radius: 24px;
              background: ${getCardGradient()};
              border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
              backdrop-filter: blur(10px);
              position: relative;
              overflow: hidden;
            ">
              <div style="
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 4px;
                background: linear-gradient(90deg, #ff3b6f, #ff9f1c, #eab308, #2ec4b6);
              "></div>
              
              <h3 style="
                margin: 0 0 24px;
                font-size: 20px;
                font-weight: 700;
                display: flex;
                align-items: center;
                color: ${isDarkMode ? '#ffffff' : '#171718'};
                gap: 12px;
              ">
                <span style="
                  display: inline-block;
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  background: #8b5cf6;
                  box-shadow: 0 0 20px #8b5cf6;
                "></span>
                Severity Distribution
              </h3>
              
              <div style="display: flex; justify-content: space-around; align-items: center;">
                ${severityBadge('critical', stats.critical)}
                ${severityBadge('high', stats.high)}
                ${severityBadge('medium', stats.medium)}
                ${severityBadge('low', stats.low)}
              </div>
  
            </div>
          </div>
        `;
  
          pages.push(page1);
  
          // PAGE 2 - Categories Summary (with proper spacing)
          let page2 = `
          <div style="
            color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            page-break-before: always;
            position: relative;
          ">
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              margin-bottom: 28px;
              padding-bottom: 16px;
              border-bottom: 2px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
            ">
              <div>
                <h2 style="
                  margin: 0 0 8px;
                  font-size: 28px;
                  font-weight: 800;
                  letter-spacing: -0.5px;
                  color: ${isDarkMode ? '#ffffff' : '#171718'};
                  margin:5px;
                  padding:0 20px
                  width: 100%;
                ">
                  Security Categories
                </h2>
                <p style="margin: 0; font-size: 14px; opacity: 0.6; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
                  Detailed analysis by security domain
                </p>
              </div>
              <div style="
                padding: 8px 16px;
                background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
                border-radius: 100px;
                font-size: 13px;
                font-weight: 600;
              ">
                ${normalizedCategories?.length || 0} Categories · ${totalFindings} Total Findings
              </div>
            </div>
  
            <!-- Categories Table -->
            <div style="
              border-radius: 24px;
              overflow: hidden;
              border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
              margin-bottom: 28px;
            ">
              <table style="
                width: 100%;
                border-collapse: collapse;
                font-size: 14px;
              ">
                <thead>
                  <tr style="
                    background: ${isDarkMode ? 'rgba(139, 92, 246, 0.2)' : '#f1f5f9'};
                    color: ${isDarkMode ? '#e2e8f0' : '#475569'};
                    font-weight: 700;
                    text-transform: uppercase;
                    font-size: 11px;
                    letter-spacing: 1px;
                  ">
                    <th style="padding: 16px 12px; text-align: left;">Category</th>
                    <th style="padding: 16px 12px; text-align: center;">Critical</th>
                    <th style="padding: 16px 12px; text-align: center;">High</th>
                    <th style="padding: 16px 12px; text-align: center;">Medium</th>
                    <th style="padding: 16px 12px; text-align: center;">Low</th>
                    <th style="padding: 16px 12px; text-align: center;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${normalizedCategories?.map(buildRow).join('')}
                </tbody>
              </table>
            </div>
            <div id="page2-findings-area"></div>
        `;
  
          const MAX_PAGE_HEIGHT = 1000;
          const measureHeight = (html) => {
            const testDiv = document.createElement('div');
            testDiv.style.width = '794px';
            testDiv.style.padding = '40px';
            testDiv.style.boxSizing = 'border-box';
            testDiv.style.visibility = 'hidden';
            testDiv.style.position = 'absolute';
            testDiv.style.fontFamily = 'Inter, sans-serif';
            testDiv.innerHTML = html;
            document.body.appendChild(testDiv);
            const height = testDiv.offsetHeight;
            document.body.removeChild(testDiv);
            return height;
          };
  
          let lastCategory = null;
          const buildCategoryHeader = (category, isContinuation = false) => `
          <h3 style="
            margin: 32px 0 20px;
            color: ${isDarkMode ? '#ffffff' : '#171718'};
            font-size: 22px;
            font-weight: 700;
            display: flex;
            align-items: center;
            gap: 12px;
          ">
            <span style="
              display: inline-block;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #8b5cf6;
              box-shadow: 0 0 20px #8b5cf6;
            "></span>
            ${category}${isContinuation ? ' <span style="font-size: 14px; opacity: 0.5; font-weight: 400;">(Continued)</span>' : ''}
          </h3>
          <p style="margin: -15px 0 20px 20px; font-size: 14px; opacity: 0.6; color: ${isDarkMode ? '#94a3b8' : '#64748b'};">
            ${isContinuation ? 'Continuation of security findings' : 'Analysis of findings in this category'}
          </p>
        `;
  
          const renderFindingCard = (finding) => {
            const severityColor = finding.severity === 'critical' ? '#ef4444' :
              finding.severity === 'high' ? '#f97316' :
                finding.severity === 'medium' ? '#eab308' : '#3b82f6';
            const severityIcon = finding.severity === 'critical' ? '🔴' :
              finding.severity === 'high' ? '🟠' :
                finding.severity === 'medium' ? '🟡' : '🔵';
  
            return `
            <div style="
              margin-bottom: 16px;
              padding: 20px;
              border-radius: 20px;
              background: ${getCardGradient()};
              border: 1px solid ${severityColor}40;
              position: relative;
              overflow: hidden;
              width: 100%;
              box-sizing: border-box;
            ">
              <div style="
                position: absolute;
                top: 0;
                left: 0;
                width: 4px;
                height: 100%;
                background: ${severityColor};
              "></div>
              <div style="display: flex; gap: 16px; margin-left: 8px;">
                <div style="font-size: 24px;">${severityIcon}</div>
                <div style="flex: 1;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                    <span style="
                      font-weight: 700;
                      font-size: 16px;
                      color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
                    ">${finding.title}</span>
                     <div style="display: flex; gap: 8px; align-items: center;">
                      <span style="
                        padding: 4px 12px;
                        border-radius: 100px;
                        background: ${severityColor}20;
                        color: ${severityColor};
                        font-size: 11px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                      ">${finding.severity}</span>
                      ${(finding.severity !== 'low' && finding.severity !== 'info') ? `
                      <span style="
                        padding: 4px 10px;
                        border-radius: 100px;
                        background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
                        color: ${isDarkMode ? '#94a3b8' : '#64748b'};
                        font-size: 10px;
                        font-weight: 700;
                        border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
                      ">IMPACT: ${finding.impact || (finding.severity === 'critical' ? '25' : finding.severity === 'high' ? '15' : '10')} PTS</span>
                      ` : ''}
                    </div>
                  </div>
                  <div style="
                    font-size: 13px;
                    color: ${isDarkMode ? '#94a3b8' : '#64748b'};
                    line-height: 1.6;
                  ">${finding.description}</div>
                  <div style="
                    margin-top: 12px;
                    font-size: 12px;
                    color: ${isDarkMode ? '#a78bfa' : '#8b5cf6'};
                    display: flex;
                    align-items: center;
                    gap: 6px;
                  ">
                    <span>${finding.icon || '🔍'}</span>
                    <span>${finding.category}</span>
                  </div>
                </div>
              </div>
            </div>
          `;
          };
  
          let page2FindingsHtml = '';
          let currentFindingsIndex = 0;
  
          for (let i = 0; i < allFindings.length; i++) {
            const finding = allFindings[i];
            let prospectiveHtml = page2FindingsHtml;
            let headerAdded = false;
  
            if (finding.category !== lastCategory) {
              prospectiveHtml += buildCategoryHeader(finding.category);
              headerAdded = true;
            }
            prospectiveHtml += renderFindingCard(finding);
  
            const fullPage2Height = measureHeight(page2 + prospectiveHtml);
  
            if (fullPage2Height > MAX_PAGE_HEIGHT) {
              break;
            } else {
              if (headerAdded) lastCategory = finding.category;
              page2FindingsHtml = prospectiveHtml;
              currentFindingsIndex = i + 1;
            }
          }
  
          pages.push(page2 + page2FindingsHtml);
  
          while (currentFindingsIndex < allFindings.length) {
            let currentPageHtml = '';
            let pageLastCategory = null;
            let itemsOnPage = 0;
  
            while (currentFindingsIndex < allFindings.length) {
              const finding = allFindings[currentFindingsIndex];
              let prospectiveHtml = currentPageHtml;
              let headerAdded = false;
              let isContinuation = false;
  
              if (finding.category !== pageLastCategory) {
                isContinuation = finding.category === lastCategory;
                prospectiveHtml += buildCategoryHeader(finding.category, isContinuation);
                headerAdded = true;
              }
  
              prospectiveHtml += renderFindingCard(finding);
              const h = measureHeight(prospectiveHtml);
  
              if (h > MAX_PAGE_HEIGHT && itemsOnPage > 0) {
                break;
              } else {
                if (headerAdded) {
                  lastCategory = finding.category;
                  pageLastCategory = finding.category;
                }
                currentPageHtml = prospectiveHtml;
                currentFindingsIndex++;
                itemsOnPage++;
              }
            }
  
            pages.push(`
            <div class="page" style="
              color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
              padding: 40px;
              width: 794px;
              box-sizing: border-box;
            ">
              <div style="width: 100%;">
                ${currentPageHtml}
              </div>
            </div>
          `);
          }
  
          return pages;
        };
  
        // Load PDF libraries if needed
        if (!window.html2canvas || !window.jspdf) {
          await loadPDFLibraries();
        }
  
        // Create temporary container
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.width = '794px';
        tempContainer.style.padding = '32px';
        tempContainer.style.background = getBgGradient();
        tempContainer.style.fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, sans-serif';
        document.body.appendChild(tempContainer);
  
        // Generate paginated content
        const pages = createPaginatedContent();
  
        // Render each page
        const canvases = [];
        for (let i = 0; i < pages.length; i++) {
          tempContainer.innerHTML = pages[i];
  
          // Add spacing at bottom to prevent footer overlap
          const spacer = document.createElement('div');
          spacer.style.height = '40px';
          tempContainer.appendChild(spacer);
  
          const canvas = await window.html2canvas(tempContainer, {
            scale: 2.5,
            useCORS: true,
            allowTaint: false,
            backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
            logging: false,
            windowWidth: 794,
            onclone: (clonedDoc) => {
              // Ensure all fonts are loaded
              const style = clonedDoc.createElement('style');
              style.textContent = `
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
              * { font-family: 'Inter', sans-serif !important; }
            `;
              clonedDoc.head.appendChild(style);
            }
          });
  
          canvases.push(canvas);
        }
  
        document.body.removeChild(tempContainer);
  
        // Create PDF
        const pdf = new window.jspdf.jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          hotfixes: ['px_scaling']
        });
  
        const margin = 15;
        const pageWidth = 210;
        const contentWidth = pageWidth - margin * 2;
        const provider =null;
        const pageHeight = 297; // A4 height in mm
        const headerMargin = 15;
        const headerFontSize = 10;
        const headerY = 10;     // Y-position for the header text
  
        // Left: website link
        if (provider?.website) {
          pdf.setFontSize(headerFontSize);
          pdf.setTextColor(isDarkMode ? 200 : 50);
          pdf.text(provider.website, headerMargin, headerY);
        }
  
        // Right: logo or provider name
        if (provider) {
          if (provider.logo) {
            // Add logo
            const logoSize = 18;
            pdf.addImage(provider.logo, 'PNG', 210 - headerMargin - logoSize, headerY - 5, logoSize, logoSize);
          } else if (provider.name) {
            // Add provider name (split if too long)
            pdf.setFontSize(headerFontSize);
            pdf.setTextColor(isDarkMode ? 200 : 50);
            const maxWidth = 210 - headerMargin * 2;
            const splitName = pdf.splitTextToSize(provider.name, maxWidth);
            splitName.forEach((line, idx) => {
              pdf.text(line, 210 - headerMargin, headerY + idx * (headerFontSize + 2), { align: 'right' });
            });
          }
        }
        for (let i = 0; i < canvases.length; i++) {
          const canvas = canvases[i];
          const scale = contentWidth / canvas.width;
          const pageHeightPx = (297 - margin * 2) / scale;
  
          if (i > 0) pdf.addPage();
          // Fill full page background
          pdf.setFillColor(isDarkMode ? '#0f172a' : '#f8fafc');
          pdf.rect(0, 0, 210, 297, 'F');
  
          pdf.addImage(
            canvas.toDataURL('image/png'),
            'PNG',
            margin,
            margin,
            contentWidth,
            canvas.height * scale,
            undefined,
            'FAST'
          );
  
  
          // Gradient line
          pdf.setDrawColor(139, 92, 246);
          pdf.setLineWidth(0.5);
          pdf.line(margin, 297 - 22, pageWidth - margin, 297 - 22);
  
          // Main footer text
          pdf.setFontSize(8);
          pdf.setTextColor(isDarkMode ? '#94a3b8' : '#64748b');
          pdf.setFont('helvetica', 'normal');
  
          const footerLeft = `${provider?.name || ''}`;
          const footerCenter = `${provider?.address || ''}`;
          const footerRight = `${provider?.website || ''}`;
          const bottom = `Page ${i + 1} of ${canvases.length} · ${new Date().toLocaleDateString()}`;
  
          const footerY = 297 - 13;
          pdf.text(footerLeft, margin, footerY);
          pdf.text(footerCenter, pageWidth / 2, footerY, { align: 'center' });
          pdf.text(footerRight, pageWidth - margin, footerY, { align: 'right' });
  
          pdf.setFontSize(7);
          pdf.text(bottom, pageWidth / 2, footerY + 6, { align: 'center' });
  
  
        }
  
        // Save with futuristic filename
        const timestamp = new Date().toISOString().split('T')[0];
        const sanitizedDomain = domain.replace(/[^a-z0-9]/gi, '-').toLowerCase();
        pdf.save(`security-scan-${sanitizedDomain}-${timestamp}.pdf`);
  
        showToast('Security report generated successfully', 'success');
  
      } catch (err) {
        console.error('PDF export failed:', err);
        showToast('Failed to generate PDF: ' + err.message, 'error');
      }
      setExporting(false);
    };

  return (
    <Container className="py-5">
      <Card
        className={`border-0 shadow-lg ${
          isDarkMode ? "bg-dark text-light" : "bg-white"
        }`}
        style={{ maxWidth: 950, margin: "auto", borderRadius: 16 }}
      >
        <Card.Body>
          <h3 className={`text-center mb-4 fw-semibold ${
            isDarkMode ? "bg-dark text-light" : "bg-white"
          }`}>
            Domain Security Scanner
          </h3>
          <div className={`mt-3 mb-4 ${isDarkMode ? "bg-dark text-light" : "text-muted"}`}>
            <p className={`${
              isDarkMode ? "bg-dark text-light" : "text-muted"
            }`}>
              This scan looks at your domain the way a threat actor might, analyzing your
              <strong> DNS configuration</strong>, <strong>email security (SPF, DMARC)</strong>,
              <strong> SSL/TLS and HTTPS setup</strong>, <strong>security headers</strong>, and
              <strong> hosting infrastructure</strong> to identify misconfigurations and
              potential external attack paths.
            </p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleScan} className="mb-4">
            <Form.Group>
              <Form.Label style={{color: isDarkMode ? "white" : "black"}}>Domain</Form.Label>
              <Form.Control
                placeholder="example.com"
                value={domain}
                disabled={loading}
                onChange={(e) => setDomain(e.target.value)}
                className={isDarkMode ? "bg-secondary text-light border-0 mb-3" : "mb-3"}
              />
              
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label style={{color: isDarkMode ? "white" : "black", margin: 0}}>
                  Select Organization
                </Form.Label>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => {
                    resetOrgForm();
                    setShowOrgModal(true);
                  }}
                >
                  <Building size={14} className="me-1" />
                  New Organization
                </Button>
              </div>
              
              <Form.Select
                value={selectedOrganization}
                onChange={(e) => setSelectedOrganization(e.target.value)}
                required
                className={isDarkMode ? 'bg-dark text-light placeholder-light mb-3' : 'mb-3'}
                disabled={organizations.length === 0}
              >
                {organizations.length === 0 && (
                  <option value="" disabled>No organizations available. Click "New Organization" to create one.</option>
                )}
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </Form.Select>
              
              <Form.Text className={isDarkMode ? "text-light" : "text-muted"}>
                Automatic IP discovery uses this organization's name, website and managed inventory to confirm ownership. Its registered company name helps matching when available; a company name is optional.
              </Form.Text>
              {organizations.length === 0 && (
                <Form.Text className="text-warning mb-3 d-block">
                  You need to create an organization before scanning.
                </Form.Text>
              )}
              
              <p className="m-2">Select Modules:</p>
              <div className="d-flex gap-2 m-3 flex-wrap">
                <Badge
                  pill
                  bg={allSelected ? "danger" : "success"}
                  style={{ cursor: "pointer" }}
                  onClick={toggleAllModules}
                >
                  {allSelected ? "Unselect All" : "Select All"}
                </Badge>
                {MODULES.map((m) => (
                  <Badge
                    key={m.id}
                    pill
                    bg={modules.includes(m.id) ? "primary" : "secondary"}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleModule(m.id)}
                  >
                    {m.label}
                  </Badge>
                ))}
              </div>
            </Form.Group>
             <PublicIpOptions target={domain} enabled={modules.includes("publicIp")}
                           mode={publicIpMode} onModeChange={setPublicIpMode}
                           value={publicIp} onChange={setPublicIp} disabled={loading} />

            {/* {modules.includes("breach") && (
              <Form.Group className="mb-3">
                <Form.Label style={{ color: isDarkMode ? "white" : "black" }}>
                  HIBP Emails
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="user1@example.com user2@example.com"
                  value={hibpEmails}
                  disabled={loading}
                  onChange={(e) => setHibpEmails(e.target.value)}
                  className={isDarkMode ? "bg-secondary text-light border-0" : ""}
                />
                <Form.Text muted>
                  Enter one or more email addresses separated by spaces.
                </Form.Text>
              </Form.Group>
            )} */}

            <Button
              type="submit"
              className="w-100 mt-3"
              disabled={!domain || !modules.length || loading || selectedOrganization === "" || organizations.length === 0}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Scanning...
                </>
              ) : (
                "Start Scan" 
              )}
            </Button>
          </Form>

          {results && (
            <>
              {/* Summary */}
              <div className="float-end mb-3 d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={previewFullReport}
                  className="d-flex align-items-center gap-2 px-4 py-2 fw-semibold shadow-sm"
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Preview Full Report
                </Button>
                <Button
                              variant="primary"
                              onClick={exportPdf}
                              disabled={exporting}
                              className="d-flex float-left align-items-center gap-2 px-4 py-2 fw-semibold shadow-sm"
                            >
                              {exporting ? (
                                <>
                                  <Spinner size="sm" />
                                  Generating PDF...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-file-earmark-pdf-fill"></i>
                                  Export Full Report
                                </>
                              )}
                            </Button>
              </div>

              <div className="d-flex flex-wrap gap-3 mb-4">
                <Badge bg={severityVariant(results.riskScore)} pill>
                  Risk: {results.riskScore}/100
                </Badge>
                <Badge bg="danger" pill>
                  Critical: {results.criticalCount}
                </Badge>
                <Badge bg="success" pill>
                  Passed: {results.passedCount}
                </Badge>
              </div>
            </>
          )}

          <hr className="my-5" />
    
        </Card.Body>
      </Card>

      {/* Organization Creation Modal */}
      <Modal 
        show={showOrgModal} 
        onHide={() => {
          setShowOrgModal(false);
          resetOrgForm();
        }} 
        centered 
        size="lg"
        data-bs-theme={isDarkMode ? "dark" : "light"}
        contentClassName={isDarkMode ? "bg-dark" : ""}
        className={`organization-modal ${isDarkMode ? "bg-dark" : "light"}`}
      >
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
          <Modal.Title className={isDarkMode ? "text-light" : ""}>
            <Building className="me-2" size={20} />
            Create New Organization
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={createOrganization}>
          <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
            {orgError && (
              <Alert variant="danger" onClose={() => setOrgError("")} dismissible>
                {orgError}
              </Alert>
            )}
            {orgMessage && (
              <Alert variant="success" onClose={() => setOrgMessage("")} dismissible>
                {orgMessage}
              </Alert>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Organization Name *</Form.Label>
              <Form.Control
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="e.g., Acme Inc."
                required
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Website</Form.Label>
              <Form.Control
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Primary Contact Person *</Form.Label>
              <Form.Control
                type="text"
                value={primaryPerson}
                onChange={(e) => setPrimaryPerson(e.target.value)}
                placeholder="Full name"
                required
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Primary Email *</Form.Label>
              <Form.Control
                type="email"
                value={primaryEmail}
                onChange={(e) => setPrimaryEmail(e.target.value)}
                placeholder="contact@example.com"
                required
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Primary Phone</Form.Label>
              <Form.Control
                type="tel"
                value={primaryPhone}
                onChange={(e) => setPrimaryPhone(e.target.value)}
                placeholder="+1 234 567 8900"
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
            <Button variant="secondary" onClick={() => {
              setShowOrgModal(false);
              resetOrgForm();
            }}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              disabled={creatingOrg || !organizationName || !primaryPerson || !primaryEmail}
            >
              {creatingOrg ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                "Create Organization"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default DomainScanner;